/**
 * Caption seam handling for TTS-paced transcripts (FLO-531).
 *
 * Cartesia streams captions as `TTSTextFrame`s that carry ONE BARE WORD and no
 * inter-frame whitespace, so the SDK has to re-insert the space at every seam.
 * That is true for Hindi exactly as it is for English: in the installed pipecat
 * (`pipecat/services/cartesia/tts.py`), `_normalize_word_timestamps` only groups
 * characters into a single frame — and `_word_timestamps_include_inter_frame_
 * spaces()` only returns True — when `_is_chinese_or_japanese_language()` is
 * true, i.e. for base language "zh"/"ja". Every other language, Hindi included,
 * takes the `else` branch: each Cartesia word timestamp becomes its own frame,
 * with its spacing stripped.
 *
 * So the rule is the legacy one — inject a space unless one side of the seam
 * already has it — and 'नमस्ते' + 'मैं' must render 'नमस्ते मैं'. The sub-word
 * conjunct-splitting hazard an earlier revision guarded against cannot occur on
 * this path: a frame is a whole word, never a fragment of one, so no seam ever
 * falls between a consonant and its matra/virama.
 *
 * The ONLY exception is the pair of scripts pipecat itself special-cases. Han
 * and Kana don't separate words with spaces, and their frames are the ones
 * stamped `includes_inter_frame_spaces=True` (they already carry whatever
 * spacing they need), so injecting there would add whitespace that the language
 * never uses. Japanese sessions are what exercise this guard.
 *
 * Getting this wrong is not cosmetic. The accumulated string is also what
 * `onBotStoppedSpeaking` pushes into `transcriptHistory` (onboarding-sdk.ts), and
 * `captionSource` is 'tts' for the whole demo — a dropped seam space poisons the
 * stored transcript and everything downstream of it, not just the live caption.
 *
 * Only the TTS-paced path uses this. The LLM-paced path appends verbatim and
 * must stay that way (FLO-505): LLM chunks split mid-word ("central|ize") and
 * carry their own spacing, so a seam space there mangles every bubble.
 */

/**
 * Scripts that do not separate words with spaces — the same set pipecat's
 * Cartesia service treats as self-spacing (`{"zh", "ja"}`). Devanagari is
 * deliberately NOT here: Hindi is space-separated and its frames are bare words.
 */
const SELF_SPACING_SCRIPT = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u;

/**
 * Whether to insert a space between an accumulated caption and the next TTS
 * frame. This is the pre-FLO-531 rule verbatim ("neither side already has a
 * space"), minus seams that touch a self-spacing script.
 *
 * An empty `next` returns false rather than the legacy `true`: the caller
 * (`onBotTtsText`) guards on `if (data.text)`, so the case is unreachable, and
 * returning true there would append a bare trailing space to the transcript.
 */
export function shouldInjectCaptionSpace(accumulated: string, next: string): boolean {
  if (!accumulated || !next) return false;
  if (accumulated.endsWith(' ') || next.startsWith(' ')) return false;
  const left = accumulated[accumulated.length - 1];
  const right = next[0];
  return !SELF_SPACING_SCRIPT.test(left) && !SELF_SPACING_SCRIPT.test(right);
}

/** Append a TTS frame to the accumulated caption, re-inserting the seam space. */
export function appendCaptionFrame(accumulated: string, next: string): string {
  return shouldInjectCaptionSpace(accumulated, next) ? `${accumulated} ${next}` : accumulated + next;
}
